import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import styled from 'styled-components';
import { get } from 'lodash';
import { FiMenu } from 'react-icons/fi';
import { HotkeysTarget2, Position } from '@blueprintjs/core';
import copy from 'copy-to-clipboard';

import { showSuccessToast } from 'core/components/toast';
import { Box, Button, Flex, Icon, Popover, Text, Tooltip } from 'core/components';
import { TAB } from 'core/util/keyCodes';

import TenantLogo from 'app/views/mkpConfig/components/TenantLogo';
import ReportIssuePopover from 'app/views/reportIssue/ReportIssuePopover';
import DiscoveryProgressChip from 'app/views/metrics/components/DiscoveryProgressChip';
import { getModifierSymbol } from 'core/util/platform';

import { ReactComponent as PersonIcon } from 'app/assets/icons/person.svg';
import { ReactComponent as OfficeIcon } from 'app/assets/icons/office.svg';

import { generateSpoofUrl } from 'app/util/generateSpoofUrl';
import { PiSparkleFill } from 'react-icons/pi';
import { HotkeyGroup } from 'app/util/constants/hotkeys';
import lazyLoad from 'core/util/lazyLoad';
import KentikLogo from '../KentikLogo';
import ProductUpdatesButton from './ProductUpdatesButton';
import UserMenu from './UserMenu';
import CompanyMenu from './CompanyMenu';
import NotificationsButton from './NotificationsButton';
import AIAdvisorButton from './AIAdvisorButton';
import SearchInput from './SearchInput';

const SudoDialog = lazyLoad(() => import('app/views/sudo/SudoDialog'));
const KDEDebugModeDialog = lazyLoad(() => import('./KDEDebugModeDialog'));
const EnvironmentSelector = lazyLoad(() => import('app/components/demo/EnvironmentSelector'));

export const NAVBAR_HEIGHT = 60;

const NavWrapper = styled(Box)`
  position: fixed;
  top: 0;
  height: ${NAVBAR_HEIGHT}px;
  width: 100vw;
  z-index: 10;
  background-color: ${({ menuOpen, theme: { colors } }) =>
    menuOpen ? colors.navbarBackgroundOpen : colors.navbarBackground};
`;

const SearchInputWrapper = styled(Box)`
  margin-right: 24px;
  width: 325px;
  position: relative;
  z-index: ${({ $isOpen }) => ($isOpen ? 120 : 'auto')};
  transition: all 300ms cubic-bezier(0.33, 1, 0.68, 1);

  ${({ $isOpen }) =>
    $isOpen &&
    `
    position: fixed;
    left: 50%;
    top: 14px;
    transform: translateX(-50%);
    width: 650px;
    margin-right: 0;
  `}

  .bp4-input-action {
    transition: opacity 0.15s;
    opacity: ${({ $isOpen }) => ($isOpen ? 0 : 0)};
    pointer-events: ${({ $isOpen }) => ($isOpen ? 'none' : 'auto')};
  }

  &:hover .bp4-input-action {
    opacity: ${({ $isOpen }) => ($isOpen ? 0 : 1)};
  }
`;

const EnvBadgeWrapper = styled(Box)`
  border-radius: 18px;
  font-size: 12px;
  padding: 0 8px;
  line-height: 18px;
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: #ffffff;
  background-color: ${({ theme }) => theme.colors.kentik};
  font-family: ${({ theme }) => theme.fonts.monospace};
  text-transform: lowercase;
`;

function EnvBadge({ openConfig, isNext, isPresetCompany }) {
  const { region: env } = openConfig;

  if (env === 'US' && !isNext) {
    return null;
  }

  return (
    <EnvBadgeWrapper>
      {env !== 'US' && env} {isNext && 'next'} {isPresetCompany && 'presets'}
    </EnvBadgeWrapper>
  );
}

const KeyboardKey = styled.kbd`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 6px;
  font-size: 11px;
  font-family: ${({ theme }) => theme.fonts.monospace};
  font-weight: 500;
  line-height: 1;
  color: #e0e0e0;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  min-width: 20px;
`;

const KeyboardSeparator = styled.span`
  color: rgba(255, 255, 255, 0.4);
  font-size: 10px;
  margin: 0 2px;
`;

function KeyboardShortcut() {
  return (
    <Flex alignItems="center" gap="2px">
      <KeyboardKey>{getModifierSymbol()}</KeyboardKey>
      <KeyboardSeparator>+</KeyboardSeparator>
      <KeyboardKey>/</KeyboardKey>
    </Flex>
  );
}

export function NavButton(props) {
  const { disabled } = props; // disabled,
  // THIS IS probably not an ideal use of the prop "disabled" bc its being reflected in aria.
  // its really just "MENU OPEN" or "MENU CLOSED" and should be a different prop
  return (
    <Button
      color={disabled ? 'white.5' : 'white.10'}
      textColor={disabled ? 'white.3' : 'white.8'}
      fontWeight="heavy"
      minimal
      {...props}
    />
  );
}

@inject('$auth', '$app', '$companySettings', '$discovery', '$users', '$journeys')
@withRouter
@observer
class Navbar extends Component {
  state = {
    userMenuOpen: false,
    companyMenuOpen: false
  };

  get hotkeys() {
    const { $app, $auth, match, onMenuClose, menuOpen } = this.props;

    const hotkeys = [
      {
        allowInInput: true,
        combo: 'mod + /',
        global: true,
        group: HotkeyGroup.General,
        label: 'Open Search',
        preventDefault: true,
        onKeyDown: (e) => this.handleToggleSearch(e, 'keyboard')
      },
      {
        combo: 'shift + s',
        global: true,
        group: HotkeyGroup.General,
        label: 'Open Search (Alternative)',
        preventDefault: true,
        onKeyDown: (e) => this.handleToggleSearch(e, 'keyboard')
      },
      {
        combo: 'shift + e',
        global: true,
        group: HotkeyGroup.Navigation,
        label: 'Data Explorer',
        preventDefault: true,
        onKeyDown: () => this.handleNavShortcut('/v4/core/explorer', 'Data Explorer')
      },
      {
        combo: 'shift + l',
        global: true,
        group: HotkeyGroup.Navigation,
        label: 'Library',
        preventDefault: true,
        onKeyDown: () => this.handleNavShortcut('/v4/library', 'Library')
      },
      {
        combo: 'shift + o',
        global: true,
        group: HotkeyGroup.Navigation,
        label: 'Observation Deck',
        preventDefault: true,
        onKeyDown: () => this.handleNavShortcut('/v4/home', 'Observation Deck')
      }
    ];

    if (menuOpen && onMenuClose) {
      hotkeys.push({
        combo: 'esc',
        global: true,
        group: HotkeyGroup.General,
        label: 'Close Menu',
        onKeyDown: onMenuClose
      });
    }

    if ($auth.hasSudo || $auth.isSpoofed) {
      hotkeys.push({
        combo: 'mod + option + c',
        global: true,
        group: HotkeyGroup.Admin,
        label: 'Copy Kentik Admin URL',
        preventDefault: true,
        onKeyDown: () => {
          const url = generateSpoofUrl($auth, { persist: true, match });
          copy(url);
          showSuccessToast('URL copied');
        }
      });
    }
    if (this.reportIssuePopoverOpen) {
      hotkeys.push({
        combo: 'esc',
        global: true,
        group: HotkeyGroup.General,
        label: 'Close Support Popover',
        preventDefault: true,
        onKeyDown: () => $app.toggleReportIssuePopover(false)
      });
    }

    return hotkeys;
  }

  handleToggleSearch = (e, method) => {
    const { $app, menuOpen, onMenuClose } = this.props;
    const keyCode = e?.keyCode || null;
    // dont open if we are tabbing through the page
    if (method !== 'keyboard' || !e || (keyCode && keyCode !== TAB)) {
      document.activeElement.blur();
      $app.toggleSearch(method);
      if (menuOpen) {
        onMenuClose();
      }
    }
  };

  handleNavShortcut = (url, toast) => {
    const { history } = this.props;
    if (toast) {
      showSuccessToast(`Shortcut: Go to ${toast}`);
    }
    history.push(url);
  };

  handleHomeClick = () => {
    const { history, $app } = this.props;
    history.push(`${$app.isSubtenant ? '/v4/landing' : '/v4'}`);
  };

  handleToggleUserMenu = (force) => {
    this.setState((prevState) => ({ userMenuOpen: force !== undefined ? force : !prevState.userMenuOpen }));
  };

  handleToggleCompanyMenu = (force) => {
    this.setState((prevState) => ({ companyMenuOpen: force !== undefined ? force : !prevState.companyMenuOpen }));
  };

  onUserMenuInteraction = (nextState) => this.setState({ userMenuOpen: nextState });

  onCompanyMenuInteraction = (nextState) => this.setState({ companyMenuOpen: nextState });

  handleFeedbackInteraction = (isOpen) => {
    const { $app } = this.props;
    $app.toggleReportIssuePopover(isOpen);
  };

  render() {
    const {
      $app,
      $auth,
      $companySettings,
      $discovery,
      $journeys,
      $users,
      menuOpen,
      onMenuClose,
      onMenuToggle,
      locked,
      location,
      ...wrapperProps
    } = this.props;
    const { reportIssuePopoverOpen } = $app;

    const { userMenuOpen, companyMenuOpen } = this.state;

    let LogoCmp = <KentikLogo style={{ width: 125, height: 26 }} isNavbar />;
    let feedbackEnabled = true;

    if ($app.isSubtenant) {
      if (get($auth.openConfig, 'subtenancy.config.icon', false)) {
        LogoCmp = <TenantLogo companyId={$auth.getActiveUserProperty('company_id')} height={21} mt="2px" />;
      }
      if ($auth.hasPermission('subtenancy.advancedMode')) {
        feedbackEnabled = get($auth.openConfig, 'subtenancy.config.supportEmail', false);
      }
    }

    return (
      <HotkeysTarget2 hotkeys={this.hotkeys}>
        <NavWrapper menuOpen={menuOpen} {...wrapperProps}>
          <Flex height={NAVBAR_HEIGHT} alignItems="center" justifyContent="space-between" pl="12px" pr={3}>
            <Flex alignItems="center" flex={1}>
              {!$auth.isSharedUser && !$app.isSubtenant && (
                <NavButton
                  active={menuOpen}
                  icon={FiMenu}
                  onClick={() => onMenuToggle()}
                  disabled={locked}
                  tracker="nav-menu-toggle-btn"
                  ml="2px"
                  p="4px 8px"
                  large
                />
              )}
              {($auth.isSharedUser || locked) && LogoCmp}
              {!$auth.isSharedUser && !locked && (
                <NavButton onClick={this.handleHomeClick} ml="2px" p="4px 8px" large minWidth={142}>
                  {LogoCmp}
                </NavButton>
              )}
              {($auth.hasSudo || $auth.isSpoofed) && (
                <EnvBadge openConfig={$auth.openConfig} isNext={$app.isNext} isPresetCompany={$auth.isPresetCompany} />
              )}
            </Flex>

            <Flex alignItems="center" justifyContent="flex-end" flex={1} gap="6px">
              {!$app.isSubtenant && (
                <SearchInputWrapper $isOpen={$app.searchIsOpen}>
                  <SearchInput
                    placeholder="Search..."
                    leftIcon="search"
                    rightElement={<KeyboardShortcut />}
                    onClick={(e) => this.handleToggleSearch(e, 'mouse')}
                    onKeyDown={(e) => this.handleToggleSearch(e, 'keyboard')}
                    onContextMenu={(e) => this.handleToggleSearch(e, 'mouse')}
                    fill
                    value=""
                    onChange={() => {}}
                    $isOpen={$app.searchIsOpen}
                  />
                </SearchInputWrapper>
              )}

              {$auth.canUseAiTools &&
                $auth.hasPermission('kaizen', { overrideForSudo: false }) &&
                !location.pathname.includes($journeys.baseUrl) &&
                !$app.isSubtenant && (
                  <AIAdvisorButton minimal onClick={() => $journeys.openCopilot()}>
                    <Flex alignItems="center" gap="4px">
                      <Icon icon={PiSparkleFill} />
                      <Text color="#fff" fontWeight="bold" fontSize="14px">
                        Ask
                      </Text>
                    </Flex>
                  </AIAdvisorButton>
                )}

              <DiscoveryProgressChip />

              {!$auth.isSharedUser && (
                <>
                  {$auth.needsActivation && (
                    <Popover
                      position={Position.BOTTOM}
                      content={
                        <Box maxWidth={400} p={2} textAlign="center">
                          Check your email and click on the activation link to confirm your email address and create
                          your password.
                          <Button
                            intent="primary"
                            text="Resend Activation Email"
                            onClick={$users.resendActivationEmail}
                            loading={$auth.passwordReqInProgress}
                            mt={2}
                          />
                        </Box>
                      }
                    >
                      <NavButton text="Activate Your Account" textColor="danger" fontWeight="normal" />
                    </Popover>
                  )}

                  {$auth.isDemoUser && <EnvironmentSelector />}

                  {$auth.isDemoUser && (
                    <Box mr="4px">
                      <EnvironmentSelector />
                    </Box>
                  )}

                  {$companySettings?.demobox?.enabled && <NotificationsButton />}

                  {!$app.isSubtenant && (
                    <Tooltip content="Product Updates" position={Position.BOTTOM} openOnTargetFocus={false}>
                      <ProductUpdatesButton />
                    </Tooltip>
                  )}

                  {feedbackEnabled && (
                    <Popover
                      isOpen={reportIssuePopoverOpen}
                      position={Position.BOTTOM}
                      onInteraction={this.handleFeedbackInteraction}
                      content={<ReportIssuePopover />}
                    >
                      <Tooltip content="Help and Support" position={Position.BOTTOM} openOnTargetFocus={false}>
                        <NavButton
                          icon="help"
                          ariaLabel="Help and Support"
                          onClick={() => $app.toggleReportIssuePopover()}
                          active={reportIssuePopoverOpen}
                          aria-label="Help and Support"
                        />
                      </Tooltip>
                    </Popover>
                  )}
                  {!$app.isSubtenant && (
                    <Popover
                      position="bottom-right"
                      isOpen={companyMenuOpen}
                      onInteraction={this.onCompanyMenuInteraction}
                      boundary="viewport"
                    >
                      <NavButton
                        icon={OfficeIcon}
                        rightIcon={<Icon icon="caret-down" iconSize={16} mx="-2px" />}
                        active={companyMenuOpen}
                        onClick={this.handleToggleCompanyMenu}
                        color={$auth.isSpoofed && !$auth.isDemoUser ? 'warning' : 'white.10'}
                        aria-label="Company Menu"
                        ariaIsMenuButton
                      />
                      <CompanyMenu handle />
                    </Popover>
                  )}
                  <Popover
                    position="bottom-right"
                    isOpen={userMenuOpen}
                    onInteraction={this.onUserMenuInteraction}
                    boundary="viewport"
                  >
                    <NavButton
                      icon={PersonIcon}
                      rightIcon={<Icon icon="caret-down" iconSize={16} mx="-2px" />}
                      active={userMenuOpen}
                      onClick={this.handleToggleUserMenu}
                      color={$auth.isSpoofed && !$auth.isDemoUser ? 'warning' : 'white.10'}
                      aria-label="User Menu"
                      ariaIsMenuButton
                    />
                    <UserMenu handle />
                  </Popover>
                </>
              )}
            </Flex>
          </Flex>
        </NavWrapper>
        {$auth.isSudoer && (
          <>
            <SudoDialog />
            <KDEDebugModeDialog isOpen={$app.debugModeOpen} />
          </>
        )}
      </HotkeysTarget2>
    );
  }
}

export default Navbar;
